/**
 * /auth/choose — chooser page for users who have BOTH a Members subscription
 * AND a concierge Client Portal account. The unified magic-link mailer
 * routes them here so they can pick which dashboard to open.
 *
 * URL shape: /auth/choose?m=<members_url>&p=<portal_url>
 * Both params are URL-encoded absolute URLs pre-baked with tokens by the
 * backend. This page never talks to the API — pure navigation.
 */
import { useMemo } from "react";
import { Users, Compass, ArrowRight } from "lucide-react";

export default function AuthChoose() {
  const { membersUrl, portalUrl } = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    return { membersUrl: q.get("m") || "", portalUrl: q.get("p") || "" };
  }, []);

  // Defensive fallback — if someone lands here without params, send them home.
  if (!membersUrl && !portalUrl) {
    window.location.replace("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0D2F4D] text-[#F5F1E8] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C6D21F] font-bold mb-4">The Ditch Collective</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight mb-3" data-testid="auth-choose-title">
          Which dashboard today?
        </h1>
        <p className="text-[#B4C8DC] text-sm mb-10">
          You've got both. Pick where you want to land.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {membersUrl && (
            <a
              href={membersUrl}
              data-testid="auth-choose-members"
              className="group bg-white text-[#0D2F4D] rounded-2xl p-6 text-left hover:bg-[#C6D21F] transition-colors"
            >
              <Users size={28} className="mb-4 opacity-80" />
              <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8] group-hover:text-[#0D2F4D]">
                Members
              </p>
              <p className="font-display text-2xl font-extrabold mt-1">The Collective</p>
              <p className="text-sm text-[#6F8FA8] group-hover:text-[#0D2F4D] mt-2 leading-relaxed">
                Weekly playbooks, guides, member events, the whole membership toolkit.
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold">
                Open members area <ArrowRight size={14} />
              </span>
            </a>
          )}
          {portalUrl && (
            <a
              href={portalUrl}
              data-testid="auth-choose-portal"
              className="group bg-white text-[#0D2F4D] rounded-2xl p-6 text-left hover:bg-[#C6D21F] transition-colors"
            >
              <Compass size={28} className="mb-4 opacity-80" />
              <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8] group-hover:text-[#0D2F4D]">
                Client
              </p>
              <p className="font-display text-2xl font-extrabold mt-1">Your Move Portal</p>
              <p className="text-sm text-[#6F8FA8] group-hover:text-[#0D2F4D] mt-2 leading-relaxed">
                Your documents, checklist, and updates from Cara &amp; Laura on your move.
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold">
                Open your portal <ArrowRight size={14} />
              </span>
            </a>
          )}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] mt-8">
          This link is unique to you. Don't share it — anyone with the URL can sign in.
        </p>
      </div>
    </div>
  );
}
